import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShoppingCart, CheckCircle2, PackageCheck, Send } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { EmptyState } from "../components/EmptyState";

const STATUS_META = {
    DRAFT:     { label: "Draft",     cls: "bg-gray-100 text-gray-700 border-gray-200" },
    SUBMITTED: { label: "Submitted", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    APPROVED:  { label: "Approved",  cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    DELIVERED: { label: "Delivered", cls: "bg-blue-50 text-blue-800 border-blue-200" },
};

export default function RequisitionsAdminPage() {
    const { user } = useAuth();
    const isFinancial = user?.effective_role === "OWNER" || user?.effective_role === "HEAD";
    const [reqs, setReqs] = useState([]);
    const [sites, setSites] = useState([]);
    const [siteFilter, setSiteFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [expanded, setExpanded] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    async function refresh() {
        setLoading(true);
        try {
            const params = {};
            if (siteFilter) params.site_id = siteFilter;
            if (statusFilter) params.status = statusFilter;
            const [r, s] = await Promise.all([
                api.get("/requisitions", { params }),
                api.get("/sites"),
            ]);
            setReqs(r.data || []);
            setSites(s.data || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [siteFilter, statusFilter]);

    async function act(rid, action) {
        setBusy(`${rid}:${action}`);
        try {
            await api.post(`/requisitions/${rid}/${action}`);
            toast.success(`Marked ${action}`);
            refresh();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    }

    const siteMap = useMemo(() => Object.fromEntries((sites || []).map(s => [s.id, s.name])), [sites]);

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8" data-testid="requisitions-admin-page">
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Approval queue</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2">
                    <ShoppingCart className="w-6 h-6" /> Monthly requisitions
                </h1>
                <p className="text-sm text-gray-500 mt-1">Field managers submit their monthly requisition; Head/Owner approves and marks delivered.</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3 items-end">
                <label className="text-xs">
                    <span className="block text-gray-500 mb-1">Site</span>
                    <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white min-w-[10rem]" data-testid="site-filter">
                        <option value="">All sites</option>
                        {(sites || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </label>
                <label className="text-xs">
                    <span className="block text-gray-500 mb-1">Status</span>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white min-w-[10rem]" data-testid="status-filter">
                        <option value="">All</option>
                        {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
                <button onClick={refresh} className="text-xs text-[#1E3355] underline underline-offset-2" data-testid="refresh-btn">Refresh</button>
            </div>

            {loading ? (
                <div className="mt-6 h-40 rounded-xl bg-white border border-gray-100 animate-pulse" />
            ) : reqs.length === 0 ? (
                <EmptyState
                    icon="📋"
                    title="No requisitions match these filters"
                    subtitle="Change the filters above, or wait for the field team to submit next month's requisitions."
                    testid="no-reqs"
                />
            ) : (
                <div className="mt-6 space-y-3">
                    {reqs.map((r) => {
                        const st = STATUS_META[r.status] || STATUS_META.DRAFT;
                        const isOpen = expanded === r.id;
                        const totalLines = (r.lines || []).length;
                        const linesNeeded = (r.lines || []).filter((l) => (l.required || 0) > 0 || l.is_due).length;
                        return (
                            <div key={r.id} className="rounded-xl bg-white border border-gray-100 fx-shadow-card" data-testid={`req-row-${r.id}`}>
                                <div className="p-4 flex flex-wrap gap-3 items-center justify-between">
                                    <div className="flex-1 min-w-[15rem]">
                                        <div className="text-sm font-semibold text-[#1E3355]">
                                            {siteMap[r.site_id] || r.site_id} · <span className="font-normal">{r.month}</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {totalLines} lines · {linesNeeded} to procure
                                            {r.approved_at ? ` · approved ${fmtDate(r.approved_at)}` : ""}
                                            {r.delivered_at ? ` · delivered ${fmtDate(r.delivered_at)}` : ""}
                                        </div>
                                    </div>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border uppercase tracking-wider ${st.cls}`} data-testid={`req-status-${r.id}`}>
                                        {st.label}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => setExpanded(isOpen ? null : r.id)} className="text-xs text-[#1E3355] underline underline-offset-2" data-testid={`toggle-req-${r.id}`}>
                                            {isOpen ? "Hide lines" : "View lines"}
                                        </button>
                                        {r.status === "SUBMITTED" && isFinancial && (
                                            <button onClick={() => act(r.id, "approve")} disabled={busy === `${r.id}:approve`} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1.5" data-testid={`approve-${r.id}`}>
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                                            </button>
                                        )}
                                        {r.status === "APPROVED" && isFinancial && (
                                            <button onClick={() => act(r.id, "deliver")} disabled={busy === `${r.id}:deliver`} className="inline-flex items-center gap-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-2.5 py-1.5" data-testid={`deliver-${r.id}`}>
                                                <PackageCheck className="w-3.5 h-3.5" /> Mark delivered
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {isOpen && (
                                    <div className="border-t border-gray-100 max-h-[420px] overflow-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-[#F4F6F9] sticky top-0">
                                                <tr>
                                                    <th className="text-left p-2">Item</th>
                                                    <th className="text-left p-2">Section</th>
                                                    <th className="text-right p-2">Consumed</th>
                                                    <th className="text-right p-2">In stock</th>
                                                    <th className="text-right p-2">Required</th>
                                                    <th className="text-left p-2">Note</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(r.lines || []).map((l, i) => (
                                                    <tr key={i} className={`border-t border-gray-100 ${l.is_due ? "bg-red-50/60" : ""}`}>
                                                        <td className="p-2 font-medium">{l.item_name}</td>
                                                        <td className="p-2 text-gray-500">{l.section || l.category || "—"}</td>
                                                        <td className="p-2 text-right">{l.consumed_raw || (l.consumed_last_month ?? "—")}</td>
                                                        <td className="p-2 text-right">{l.stock_raw || (l.stock_now ?? "—")}</td>
                                                        <td className="p-2 text-right font-semibold text-[#1E3355]">{l.required_raw || l.unit_raw || (l.required ?? "—")}</td>
                                                        <td className="p-2 text-gray-500">{l.note || (l.is_due ? "DUE" : "")}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Site-scoped view where a Facility Head fills consumed/in-stock/required and submits.
export function SiteRequisitionPage() {
    const { user } = useAuth();
    // eslint-disable-next-line
    const params = new URL(window.location.href);
    const siteId = window.location.pathname.split("/")[2];
    const [site, setSite] = useState(null);
    const [reqs, setReqs] = useState([]);
    const [draft, setDraft] = useState(null);
    const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    async function refresh() {
        setLoading(true);
        try {
            const [s, r] = await Promise.all([
                api.get(`/sites/${siteId}`),
                api.get("/requisitions", { params: { site_id: siteId } }),
            ]);
            setSite(s.data);
            setReqs(r.data || []);
            // Load draft = existing DRAFT for the selected month, else null.
            const found = (r.data || []).find((x) => x.month === month && x.status === "DRAFT");
            setDraft(found || null);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [siteId, month]);

    // If no draft for this month but an APPROVED/DELIVERED exists, clone its line-items as a starting point.
    async function createDraft() {
        setSaving(true);
        try {
            const prior = (reqs || []).find((r) => r.month === month) || (reqs || [])[0];
            const seedLines = (prior?.lines || []).map((l) => ({
                item_name: l.item_name, category: l.category || null, section: l.section || null,
                consumed_last_month: l.consumed_last_month ?? null,
                stock_now: null,       // to be filled
                required: null,        // to be filled
                unit_raw: null, is_due: false, note: null,
            }));
            const { data } = await api.post("/requisitions", { site_id: siteId, month, lines: seedLines });
            setDraft(data);
            toast.success("Draft created");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    }

    async function saveLines(newLines) {
        if (!draft) return;
        setSaving(true);
        try {
            const { data } = await api.patch(`/requisitions/${draft.id}`, { site_id: siteId, month: draft.month, lines: newLines });
            setDraft(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    }

    async function submitDraft() {
        if (!draft) return;
        setSaving(true);
        try {
            await api.post(`/requisitions/${draft.id}/submit`);
            toast.success("Submitted for approval");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    }

    if (loading) return <div className="mx-auto max-w-5xl p-6"><div className="h-64 rounded-xl bg-white animate-pulse" /></div>;

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8" data-testid="site-requisition-page">
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Monthly requisition</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2">
                    <ShoppingCart className="w-6 h-6" /> {site?.name}
                </h1>
                <p className="text-sm text-gray-500 mt-1">Fill consumed / in-stock / required for each item and submit for approval.</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3 items-end">
                <label className="text-xs">
                    <span className="block text-gray-500 mb-1">Month</span>
                    <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white" data-testid="month-picker" />
                </label>
                {!draft && (
                    <button onClick={createDraft} disabled={saving} className="rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-1.5" data-testid="create-draft-btn">
                        {saving ? "Creating…" : "Start requisition for this month"}
                    </button>
                )}
                {draft && (
                    <button onClick={submitDraft} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-1.5" data-testid="submit-btn">
                        <Send className="w-4 h-4" /> Submit for approval
                    </button>
                )}
            </div>

            {draft ? (
                <DraftEditor draft={draft} onChange={saveLines} disabled={saving} />
            ) : (
                <PriorRequisitions reqs={reqs} />
            )}
        </div>
    );
}

function DraftEditor({ draft, onChange, disabled }) {
    const [lines, setLines] = useState(draft.lines || []);
    useEffect(() => { setLines(draft.lines || []); }, [draft]);

    function updateLine(i, patch) {
        const next = lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
        setLines(next);
        onChange(next);
    }
    function addLine() {
        const next = [...lines, { item_name: "", section: null, consumed_last_month: null, stock_now: null, required: null, unit_raw: null, is_due: false, note: null }];
        setLines(next);
    }

    return (
        <div className="mt-4 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
            <div className="max-h-[70vh] overflow-auto">
                <table className="w-full text-xs">
                    <thead className="bg-[#F4F6F9] sticky top-0">
                        <tr>
                            <th className="text-left p-2 w-[26%]">Item</th>
                            <th className="text-left p-2 w-[18%]">Section</th>
                            <th className="text-right p-2 w-[10%]">Consumed</th>
                            <th className="text-right p-2 w-[10%]">In stock</th>
                            <th className="text-right p-2 w-[14%]">Required</th>
                            <th className="text-center p-2 w-[6%]">DUE</th>
                            <th className="text-left p-2">Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((l, i) => (
                            <tr key={i} className={`border-t border-gray-100 ${l.is_due ? "bg-red-50/40" : ""}`} data-testid={`req-line-${i}`}>
                                <td className="p-1"><input value={l.item_name || ""} onChange={(e) => updateLine(i, { item_name: e.target.value })} className="w-full text-xs px-1.5 py-1 rounded border border-transparent hover:border-gray-200 focus:border-[#1E3355] outline-none bg-white" data-testid={`item-name-${i}`} /></td>
                                <td className="p-1"><input value={l.section || l.category || ""} onChange={(e) => updateLine(i, { section: e.target.value })} className="w-full text-xs px-1.5 py-1 rounded border border-transparent hover:border-gray-200 focus:border-[#1E3355] outline-none bg-white" /></td>
                                <td className="p-1"><input type="text" value={l.consumed_raw ?? l.consumed_last_month ?? ""} onChange={(e) => updateLine(i, { consumed_raw: e.target.value })} className="w-full text-xs text-right px-1.5 py-1 rounded border border-transparent hover:border-gray-200 focus:border-[#1E3355] outline-none bg-white" data-testid={`consumed-${i}`} /></td>
                                <td className="p-1"><input type="text" value={l.stock_raw ?? l.stock_now ?? ""} onChange={(e) => updateLine(i, { stock_raw: e.target.value, stock_now: parseFloat(e.target.value) || null })} className="w-full text-xs text-right px-1.5 py-1 rounded border border-transparent hover:border-gray-200 focus:border-[#1E3355] outline-none bg-white" data-testid={`stock-${i}`} /></td>
                                <td className="p-1"><input type="text" value={l.required_raw ?? l.required ?? ""} onChange={(e) => {
                                    const num = parseFloat(e.target.value) || null;
                                    updateLine(i, { required_raw: e.target.value, required: num, unit_raw: e.target.value });
                                }} className="w-full text-xs text-right px-1.5 py-1 rounded border border-transparent hover:border-gray-200 focus:border-[#1E3355] outline-none bg-white" data-testid={`required-${i}`} /></td>
                                <td className="p-1 text-center"><input type="checkbox" checked={l.is_due || false} onChange={(e) => updateLine(i, { is_due: e.target.checked })} data-testid={`due-${i}`} /></td>
                                <td className="p-1"><input value={l.note || ""} onChange={(e) => updateLine(i, { note: e.target.value })} className="w-full text-xs px-1.5 py-1 rounded border border-transparent hover:border-gray-200 focus:border-[#1E3355] outline-none bg-white" /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="border-t border-gray-100 p-2 flex justify-between items-center">
                <span className="text-xs text-gray-500">{lines.length} lines · {disabled ? "Saving…" : "Autosaves on edit"}</span>
                <button onClick={addLine} className="text-xs text-[#1E3355] font-semibold underline underline-offset-2" data-testid="add-line-btn">+ Add line</button>
            </div>
        </div>
    );
}

function PriorRequisitions({ reqs }) {
    if (!reqs.length) return <div className="mt-6 rounded-xl bg-white border border-gray-100 p-6 text-sm text-gray-500">No requisitions yet for this site.</div>;
    return (
        <div className="mt-6 space-y-2" data-testid="prior-reqs">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Prior requisitions</div>
            {reqs.map((r) => (
                <div key={r.id} className="rounded-md bg-white border border-gray-100 p-3 flex justify-between items-center text-sm">
                    <span><span className="font-semibold">{r.month}</span> · {(r.lines || []).length} lines</span>
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${STATUS_META[r.status]?.cls || ""}`}>{STATUS_META[r.status]?.label}</span>
                </div>
            ))}
        </div>
    );
}
